<template>
  <div class="action-list" :class="`${row2n % 2 ? 'row2n' : 'row2np1'}`">
    <div v-if="actions.length === 0" class="empty-actions">
      {{ t("modules.sqdc.components.eventActionBoard.modals.SQDCActionsField.noActions") }}
    </div>
    <template v-for="(action, index) in renderedActions" :key="action?.id ?? `empty-${index}`">
      <ActionComponent
        v-if="action != null"
        :action="action"
        :event="event"
        :archived="eventArchived"
        :class="getActionClass(index, row2n)"
        :action-types="actionTypes"
        :board-id="boardId"
        :site-id="siteId"
      />
    </template>
    <div
      v-if="inactiveActions > 0"
      class="inactive-actions lisaui:cursor-pointer"
      @click="showInactive = true"
    >
      {{ t("components.eventAction.action.ActionList.inactiveActions") }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, inject, onBeforeUnmount, type PropType, ref, watch } from "vue";
import type { EABActionTypeType, EABEventActionType, EABEventType } from "./EventActionHelper";
import ActionComponent from "./ActionComponent.vue";
import { ActionFilterInjectionKey } from "./filter/FilterTypes";
import { DateTime } from "luxon";
import { isActionActive } from "./filter/Filter";
import { SQDCActionStatus, TaskTyp } from "@/graphql.ts";

const props = defineProps({
  actions: {
    type: Array as PropType<(EABEventActionType | undefined)[]>,
    required: true,
  },
  event: {
    type: Object as PropType<EABEventType>,
    required: true,
  },
  /** defines the starting background color (striped) of the action list */
  row2n: {
    type: Number,
    required: false,
    default: 0,
  },
  actionTypes: {
    type: Array as PropType<EABActionTypeType[]>,
    required: false,
    default: () => [],
  },
  boardId: {
    type: Number,
    required: false,
    default: undefined,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
});
const emit = defineEmits<{
  renderingChange: [rendering: boolean];
}>();
const showInactive = ref(false);
const actionFilterList = inject(
  ActionFilterInjectionKey,
  ref({ updatedAt: DateTime.now(), actionFilters: [] }),
);

const statusIndex = [SQDCActionStatus.PLAN, SQDCActionStatus.DO, SQDCActionStatus.CHECK];
const activeActions = computed(() => {
  if (showInactive.value) {
    return props.actions;
  }
  return props.actions.filter((action) => {
    if (action == null) {
      return false;
    }

    return isActionActive(actionFilterList.value, {
      __typename: "Task",
      assigneeId: action.assigneeId,
      status: statusIndex.indexOf(action.status),
      createdAt: action.createdAt,
      deadline: action.deadline,
      updatedAt: action.updatedAt,
      description: action.SQDCActionTextFields.map((field) => field.value).join(" "),
      id: action.id,
      preventsArchiving: action.status !== SQDCActionStatus.ACT,
      type: TaskTyp.common,
    });
  });
});
const renderedActionCount = ref(0);
const renderedActions = computed(() => activeActions.value.slice(0, renderedActionCount.value));
const isRenderingActions = computed(() => renderedActionCount.value < activeActions.value.length);
let renderFrame: number | undefined;
let renderGeneration = 0;

function scheduleRemainingActions() {
  const generation = ++renderGeneration;
  if (renderFrame != null) {
    cancelAnimationFrame(renderFrame);
  }

  const renderNextAction = () => {
    if (generation !== renderGeneration) {
      return;
    }
    renderedActionCount.value = Math.min(renderedActionCount.value + 1, activeActions.value.length);
    if (renderedActionCount.value < activeActions.value.length) {
      renderFrame = requestAnimationFrame(renderNextAction);
    } else {
      renderFrame = undefined;
    }
  };

  if (renderedActionCount.value < activeActions.value.length) {
    renderFrame = requestAnimationFrame(renderNextAction);
  }
}

watch(
  activeActions,
  (nextActions, previousActions) => {
    const previousActionsArePrefix =
      previousActions != null &&
      previousActions.length > 0 &&
      nextActions.length >= previousActions.length &&
      previousActions.every((action, index) => nextActions[index]?.id === action?.id);

    renderedActionCount.value = previousActionsArePrefix
      ? Math.min(renderedActionCount.value, nextActions.length)
      : Math.min(1, nextActions.length);
    scheduleRemainingActions();
  },
  { immediate: true },
);
watch(isRenderingActions, (rendering) => emit("renderingChange", rendering), {
  immediate: true,
});
const inactiveActions = computed(() => {
  return props.actions.length - activeActions.value.length;
});
const eventArchived = computed(() => props.event.deletedAt != null);
function getActionClass(index: number, row2n: number) {
  return {
    row2n: (index + row2n) % 2,
    row2np1: !((index + row2n) % 2),
    borderTop: index !== 0,
  };
}

onBeforeUnmount(() => {
  renderGeneration += 1;
  if (renderFrame != null) {
    cancelAnimationFrame(renderFrame);
  }
  if (isRenderingActions.value) {
    emit("renderingChange", false);
  }
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.action-list {
  border: 1px solid var(--p-gray-900);
  border-left: none;
  border-radius: 3px;
  .borderTop {
    border-top: 1px solid lightgray;
  }
  .action:first-of-type {
    border-top: 0;
  }
  .inactive-actions {
    padding: 0.5em;
    text-align: center;
  }

  .empty-actions {
    padding: 0.75rem;
    color: var(--p-text-muted-color);
    text-align: center;
  }
}
</style>
